/**
 * math-exam — 數學考卷線上互動卷 + 純計分函式。
 *
 * 對應 listening-exam.ts(英語聽力卷)同一套形狀:四大題互動作答 → 交卷即時計分 +
 * 逐題對錯 + 正解 + 錯題回流。純計分函式 `scoreMathExam` / 答案正規化 `normalizeMathAnswer`
 * 抽出可單測(無 DOM 依賴)。
 *
 * 數學題是**填空作答**(非選擇):學生在每格輸入,交卷時與產生器算出的程式可驗證答案
 * 比對(正規化:全形/半形、空白、餘/r、; 分隔都接受)。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕
 * `academicButton`。專業介面鐵則:無音效、無 mascot、無彩光、無戲謔文案。
 *
 * 學習/遊戲分離:純學習側,只動學習數據,**絕不**碰 commanderXp / 戰鬥數值。
 */

import type {
  MathExam,
  MathSection,
  MathExamItem,
} from './math-exam-gen';
import { pageShell, academicButton } from '../ui/components';

// ───────────────────────────── 答案正規化 + 比對 ─────────────────────────────

/**
 * 正規化一個數學答案字串以利寬鬆比對:
 *   - 全形數字 → 半形、全形分號/逗號 → 半形。
 *   - 去所有空白。
 *   - 「餘 / r / R」統一成 'r'(餘數寫法等價)。
 *   - 小寫化。
 * 例:'114 餘 3' / '114餘3' / '114r3' / '114 R 3' → '114r3'。
 */
export function normalizeMathAnswer(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();
  // 全形數字 → 半形。
  s = s.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 0x30));
  // 全形 / 中文標點 → 半形。
  s = s
    .replace(/[；;]/g, ';')
    .replace(/[，,]/g, ',')
    .replace(/[／]/g, '/');
  // 餘數寫法統一。
  s = s.replace(/餘|ｒ|R/g, 'r');
  // 去空白 + 小寫。
  s = s.replace(/\s+/g, '').toLowerCase();
  return s;
}

/** 一題的「填空格」規格:每格一個欄位(label + 正解)。 */
export interface MathBlankSpec {
  /** 該格顯示提示(如「商與餘」「第一格」「答案」)。 */
  label: string;
  /** 該格正解(已是顯示用人類可讀字串)。 */
  correct: string;
}

/**
 * 一題拆成「填空格」清單(逐格批改)。每大題的格數固定:
 *   - compute:1 格(整除 = 純商「114」;有餘 = 「114 餘 3」)。
 *   - mulvert:1 格(乘積)。
 *   - arrow:2 格(第一格 mid;第二格 end)。
 *   - inverse:2 格(a÷c=b;b×c=a)。
 *   - remword:1 格(進位 = 數量;捨去 = 「N 單位,剩 R …」整串答案)。
 *   - twostep:1 格(最終數值)。
 *   - word:1 格(數值,單位選填)。
 */
export function blanksOf(item: MathExamItem): MathBlankSpec[] {
  switch (item.kind) {
    case 'compute':
      return [
        {
          label: item.remainder === 0 ? '商' : '商與餘',
          correct: item.remainder === 0 ? `${item.quotient}` : `${item.quotient} 餘 ${item.remainder}`,
        },
      ];
    case 'mulvert':
      return [{ label: '乘積', correct: String(item.product) }];
    case 'arrow':
      return [
        { label: '第一格', correct: String(item.mid) },
        { label: '第二格', correct: String(item.end) },
      ];
    case 'inverse':
      return [
        { label: `${item.product}÷${item.quotientC}`, correct: String(item.divisorB) },
        { label: `${item.divisorB}×${item.quotientC}`, correct: String(item.product) },
      ];
    case 'remword':
      // 進位 = 只填數量;捨去 = 填「N 單位,剩 R …」整串(answer 已是顯示答案)。
      return [{ label: '答案', correct: item.mode === 'ceil' ? String(item.value) : item.answer }];
    case 'twostep':
      return [{ label: '答案', correct: String(item.value) }];
    case 'word':
      return [{ label: '答案', correct: String(item.value) }];
  }
}

/** 學生對一題的作答:每格一個字串(index 對齊 blanksOf)。 */
export type ItemAnswer = string[];

/** 整卷作答:`"sectionIdx:itemIdx" → ItemAnswer`。 */
export type ExamAnswers = Record<string, ItemAnswer>;

/** 單題判定結果。 */
export interface MathItemResult {
  itemId: string;
  /** 該題每格是否答對。 */
  blanks: boolean[];
  /** 整題是否全對(所有格都對)。 */
  correct: boolean;
  /** 正解顯示字串。 */
  correctLabel: string;
  /** 學生作答顯示字串(未作答 = 空)。 */
  chosenLabel: string;
  /** 該題配分。 */
  points: number;
  /** 得分(全對 = points,否則 0)。 */
  earned: number;
  /** 該題的解析 / 驗算(顯示用)。 */
  explanation: string;
}

/** 整卷計分結果。 */
export interface MathExamScore {
  score: number;
  total: number;
  correctCount: number;
  itemCount: number;
  results: MathItemResult[];
}

/** 一題的正解顯示字串。 */
function correctLabelOf(item: MathExamItem): string {
  return item.answer;
}

/** 一題的解析 / 驗算文字。 */
function explanationOf(item: MathExamItem): string {
  switch (item.kind) {
    case 'compute':
      return `驗算:${item.check}`;
    case 'mulvert':
      return `驗算:${item.check}`;
    case 'arrow':
      return `先${item.op === '÷' ? '除' : '乘'}再用逆運算,第二格回到起始數 ${item.start}。`;
    case 'inverse':
      return `乘除互逆:${item.divisorB}×${item.quotientC}=${item.product},所以 ${item.product}÷${item.quotientC}=${item.divisorB}。`;
    case 'remword':
      return item.mode === 'ceil'
        ? `${item.dividend}÷${item.divisor}=${item.quotient} 餘 ${item.remainder};要全部裝完,餘數要進位 → ${item.quotient}+1=${item.value} ${item.unitName}。`
        : `${item.dividend}÷${item.divisor}=${item.quotient} 餘 ${item.remainder};只能整份分,答 ${item.value} ${item.unitName},剩 ${item.remainder}。`;
    case 'twostep':
      return `兩步驟:${item.expr}=${item.value} ${item.unitName}(先算 ${item.step1},再 ÷${item.c})。`;
    case 'word':
      return `算式:${item.expr}=${item.value} ${item.unitName}。`;
  }
}

/** 一格是否答對(正規化後比對)。 */
function isBlankCorrect(studentRaw: string | undefined, correct: string): boolean {
  if (studentRaw === undefined) return false;
  return normalizeMathAnswer(studentRaw) === normalizeMathAnswer(correct);
}

/**
 * 純計分:逐格批改 → 整題全對才得分。可單測(無 DOM 依賴)。
 *
 * @param exam    一份數學考卷。
 * @param answers 整卷作答(`"sectionIdx:itemIdx" → 每格字串`)。
 * @returns 得分 / 滿分 / 逐題結果。
 */
export function scoreMathExam(exam: MathExam, answers: ExamAnswers): MathExamScore {
  const results: MathItemResult[] = [];
  let score = 0;
  let total = 0;
  let correctCount = 0;
  let itemCount = 0;

  exam.sections.forEach((section, sIdx) => {
    section.items.forEach((item, iIdx) => {
      itemCount += 1;
      total += section.pointsEach;
      const specs = blanksOf(item);
      const studentBlanks = answers[`${sIdx}:${iIdx}`] ?? [];
      const blanks = specs.map((spec, bIdx) => isBlankCorrect(studentBlanks[bIdx], spec.correct));
      const allCorrect = blanks.every(Boolean);
      const earned = allCorrect ? section.pointsEach : 0;
      if (allCorrect) {
        correctCount += 1;
        score += section.pointsEach;
      }
      results.push({
        itemId: item.id,
        blanks,
        correct: allCorrect,
        correctLabel: correctLabelOf(item),
        chosenLabel: studentBlanks.filter((s) => s && s.trim() !== '').join(' , '),
        points: section.pointsEach,
        earned,
        explanation: explanationOf(item),
      });
    });
  });

  return { score, total, correctCount, itemCount, results };
}

// ───────────────────────────── 互動卷 UI ─────────────────────────────

/**
 * 交卷結果回呼,讓接點(launch)可把錯題回流錯題本。回傳 `wrongItems`。
 */
export interface MathExamWrong {
  item: MathExamItem;
  sectionKind: MathSection['kind'];
}

/**
 * Render 線上數學考卷 into `container`。四大題填空作答 → 「交卷」即時計分 + 逐題對錯。
 *
 * @param container 掛載點。
 * @param exam      一份(產生)數學考卷。
 * @param onDone    離開 / 完成回呼。
 * @param onSubmit  選填:交卷後回呼(帶分數 + 錯題),供錯題回流。
 */
export function runMathExam(
  container: HTMLElement,
  exam: MathExam,
  onDone: () => void,
  onSubmit?: (score: MathExamScore, wrong: MathExamWrong[]) => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: `${exam.scope} · 在每格寫出答案,作答後按「交卷」看分數與正解。`,
    // 答題畫面:統一 ‹ 返回 / 左緣滑動都先確認(不可靜默丟失作答)。
    onBack: onDone,
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'math-exam';
  root.style.cssText = 'display:flex;flex-direction:column;gap:22px;';
  shell.body.appendChild(root);

  // 學生作答狀態:`"sectionIdx:itemIdx" → 每格字串`。
  const answers: ExamAnswers = {};
  let submitted = false;

  exam.sections.forEach((section, sIdx) => {
    const sectionEl = document.createElement('section');
    sectionEl.className = `mx-section mx-section--${section.kind}`;
    sectionEl.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

    const head = document.createElement('div');
    head.className = 'mx-section-head';
    head.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:18px;color:var(--ef-ink);' +
      'border-left:4px solid var(--ef-accent,#2f6b4f);padding-left:10px;';
    head.textContent = `${section.label}、${section.title}`;
    sectionEl.appendChild(head);

    const instr = document.createElement('div');
    instr.className = 'mx-section-instr';
    instr.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
    instr.textContent = section.instructions;
    sectionEl.appendChild(instr);

    section.items.forEach((item, iIdx) => {
      const row = renderItem(sIdx, iIdx, item, answers);
      sectionEl.appendChild(row);
    });

    root.appendChild(sectionEl);
  });

  // ── 交卷 + 結果區 ──
  const actions = document.createElement('div');
  actions.className = 'mx-actions';
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:8px;';
  root.appendChild(actions);

  const resultEl = document.createElement('div');
  resultEl.className = 'mx-result';
  resultEl.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  root.appendChild(resultEl);

  const submitBtn = academicButton({
    label: '交卷',
    variant: 'primary',
    onClick: () => {
      if (submitted) return;
      submitted = true;
      const result = scoreMathExam(exam, answers);
      renderResult(resultEl, result);
      submitBtn.disabled = true;
      submitBtn.classList.add('mx-submit--done');
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (onSubmit) {
        const wrong: MathExamWrong[] = [];
        let flat = 0;
        exam.sections.forEach((section) => {
          section.items.forEach((item) => {
            const r = result.results[flat];
            flat += 1;
            if (r && !r.correct) wrong.push({ item, sectionKind: section.kind });
          });
        });
        onSubmit(result, wrong);
      }
    },
  });
  submitBtn.classList.add('mx-submit');
  actions.appendChild(submitBtn);

  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      shell.unmount(); // 已交卷看完分數,完成不需再確認;卸返回處理器後回上一頁。
      onDone();
    },
  });
  doneBtn.classList.add('mx-done');
  actions.appendChild(doneBtn);
}

/** 一題的容器(題號 + 題幹 + 每格輸入)。 */
function renderItem(
  sIdx: number,
  iIdx: number,
  item: MathExamItem,
  answers: ExamAnswers,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'mx-item';
  row.dataset.itemId = item.id;
  row.style.cssText =
    'display:flex;flex-direction:column;gap:10px;padding:14px 16px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);box-shadow:var(--ef-shadow-card,none);';

  const top = document.createElement('div');
  top.className = 'mx-item-top';
  top.style.cssText = 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';

  const num = document.createElement('span');
  num.className = 'mx-item-num';
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${iIdx + 1}.`;
  top.appendChild(num);

  const prompt = document.createElement('span');
  prompt.className = 'mx-item-prompt';
  prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);line-height:1.6;';
  prompt.textContent = item.prompt;
  top.appendChild(prompt);
  row.appendChild(top);

  // 填空輸入列(逐格)。
  const body = document.createElement('div');
  body.className = 'mx-item-body';
  body.style.cssText = 'display:flex;flex-wrap:wrap;gap:14px;align-items:center;';

  const specs = blanksOf(item);
  answers[`${sIdx}:${iIdx}`] = specs.map(() => '');
  specs.forEach((spec, bIdx) => {
    const field = document.createElement('label');
    field.className = 'mx-blank';
    field.style.cssText = 'display:inline-flex;align-items:center;gap:6px;font-size:14px;color:var(--ef-sub);';

    const tag = document.createElement('span');
    tag.className = 'mx-blank-label';
    tag.textContent = `${spec.label}:`;
    field.appendChild(tag);

    const input = document.createElement('input');
    input.type = 'text';
    input.className = `mx-input mx-input--${item.id}-${bIdx}`;
    input.setAttribute('inputmode', 'text');
    input.autocomplete = 'off';
    input.style.cssText =
      'min-width:5em;padding:6px 8px;font-size:15px;border:1px solid var(--ef-line,#d8d2c4);' +
      'border-radius:6px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);';
    input.addEventListener('input', () => {
      answers[`${sIdx}:${iIdx}`][bIdx] = input.value;
    });
    field.appendChild(input);
    body.appendChild(field);
  });

  row.appendChild(body);
  return row;
}

/** 結果區:總分 + 逐題對錯 + 正解 + 解析。 */
function renderResult(el: HTMLElement, result: MathExamScore): void {
  el.innerHTML = '';
  el.style.display = 'flex';

  const headline = document.createElement('div');
  headline.className = 'mx-score';
  headline.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:24px;color:var(--ef-ink);' +
    'border-top:2px solid var(--ef-line,#d8d2c4);padding-top:14px;';
  headline.textContent = `分數:${result.score} / ${result.total}(答對 ${result.correctCount} / ${result.itemCount} 題)`;
  el.appendChild(headline);

  const list = document.createElement('div');
  list.className = 'mx-result-list';
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:14px;';

  result.results.forEach((r, idx) => {
    const line = document.createElement('div');
    line.className = `mx-result-line ${r.correct ? 'mx-correct' : 'mx-wrong'}`;
    line.style.cssText = 'display:flex;flex-direction:column;gap:2px;';
    const mark = r.correct ? '✓' : '✗';
    const chosen = r.chosenLabel || '(未作答)';
    const main = document.createElement('div');
    main.style.color = r.correct ? 'var(--ef-ink)' : '#b03030';
    main.textContent = r.correct
      ? `${mark} 第 ${idx + 1} 題:${r.correctLabel}`
      : `${mark} 第 ${idx + 1} 題:你寫「${chosen}」→ 正解「${r.correctLabel}」`;
    line.appendChild(main);
    const exp = document.createElement('div');
    exp.style.cssText = 'font-size:12px;color:var(--ef-sub);';
    exp.textContent = r.explanation;
    line.appendChild(exp);
    list.appendChild(line);
  });
  el.appendChild(list);
}
